import * as Sentry from '@sentry/react'
import { createClientIncidentRef, reportClientErrorEvent } from '@frontend-shared/http'

import type { PortalProfile } from '../auth/types'

const CORRELATION_ID_STORAGE_KEY = 'cargonice-correlation-id'
const SMART_SPOT_ACTIONS = new Set(['prefill', 'enrich', 'submit', 'publish', 'draft', 'complete'])
const AI_EMAIL_ACTIONS = new Set(['publish', 'cancel', 'offer', 'delete', 'refresh'])

let telemetryInitialized = false
let unhandledRejectionBound = false

export interface TelemetryContext {
  feature?: string | undefined
  action?: string | undefined
  endpoint?: string | undefined
  method?: string | undefined
  status?: number | undefined
  errorKey?: string | undefined
  page?: string | undefined
  component?: string | undefined
  componentStack?: string | null | undefined
  prefillSourceType?: string | undefined
  prefillSourceId?: string | undefined
  requestId?: string | undefined
  missingFields?: unknown
  aiRequired?: boolean | undefined
  aiEnriched?: boolean | undefined
  smartSpotEditId?: string | number | undefined
  publicationId?: string | number | undefined
  transportId?: string | number | undefined
  messageId?: string | undefined
  transportStatus?: string | undefined
  selectedExchanges?: unknown
  payload?: unknown
  incidentRef?: string | undefined
  validationField?: string | undefined
  validationReason?: string | undefined
}

interface SanitizedTelemetryContext {
  feature?: string | undefined
  action?: string | undefined
  app?: Record<string, unknown> | undefined
  http?: Record<string, unknown> | undefined
  smartSpot?: Record<string, unknown> | undefined
  aiEmail?: Record<string, unknown> | undefined
  componentStack?: string | undefined
}

const isTelemetryEnabled = () =>
  import.meta.env.VITE_SENTRY_ENABLED === 'true' &&
  Boolean(String(import.meta.env.VITE_SENTRY_DSN || '').trim())

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const normalizeText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return undefined
}

const normalizeBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value
  }
  return undefined
}

const normalizeNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.')
    if (!normalized) {
      return undefined
    }
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const parseSampleRate = (value: unknown, fallback: number) => {
  const parsed = normalizeNumber(value)
  if (parsed == null) {
    return fallback
  }
  if (parsed < 0) {
    return 0
  }
  if (parsed > 1) {
    return 1
  }
  return parsed
}

const normalizeStringArray = (value: unknown): string[] | undefined => {
  const rawValues = Array.isArray(value) ? value : value != null ? [value] : []
  if (!rawValues.length) {
    return undefined
  }
  const normalized = rawValues
    .map((entry) => normalizeText(entry))
    .filter((entry): entry is string => Boolean(entry))
  return normalized.length ? normalized : undefined
}

const pruneObject = (value: Record<string, unknown>): Record<string, unknown> | undefined => {
  const entries = Object.entries(value).filter(([, entry]) => {
    if (entry == null) {
      return false
    }
    if (Array.isArray(entry)) {
      return entry.length > 0
    }
    if (typeof entry === 'string') {
      return entry.trim() !== ''
    }
    if (typeof entry === 'object') {
      return Object.keys(entry).length > 0
    }
    return true
  })
  if (!entries.length) {
    return undefined
  }
  return Object.fromEntries(entries)
}

const buildTracePropagationTargets = () => {
  const targets: Array<string | RegExp> = [/^\//]
  const apiBase = normalizeText(import.meta.env.VITE_API_BASE_URL)
  if (!apiBase) {
    return targets
  }
  try {
    const parsed = new URL(apiBase, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    targets.push(parsed.origin)
    targets.push(`${parsed.origin}${parsed.pathname}`)
  } catch {
    targets.push(apiBase)
  }
  return targets
}

const buildSmartSpotPayloadSnapshot = (payload: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(payload)) {
    return undefined
  }

  const bodyTypes = normalizeStringArray(payload.bodyTypes ?? payload.bodyType)
  const selectedExchanges = normalizeStringArray(payload.exchanges ?? payload.exchange)

  return pruneObject({
    requestId: normalizeText(payload.requestId),
    smartSpotEditId: normalizeText(payload.smartSpotEditId),
    loadingCountry: normalizeText(payload.loadingCountryCode ?? payload.loadingCountry),
    loadingCity: normalizeText(payload.loadingCity),
    loadingPostalCode: normalizeText(payload.loadingPostalCode ?? payload.loadingPostal),
    unloadingCountry: normalizeText(payload.unloadingCountryCode ?? payload.unloadingCountry),
    unloadingCity: normalizeText(payload.unloadingCity),
    unloadingPostalCode: normalizeText(payload.unloadingPostalCode ?? payload.unloadingPostal),
    loadingDate: normalizeText(payload.loadingDate),
    unloadingDate: normalizeText(payload.unloadingDate),
    vehicleType: normalizeText(payload.vehicleType),
    bodyTypes,
    weight: normalizeNumber(payload.weight) ?? normalizeText(payload.weight),
    length: normalizeNumber(payload.length) ?? normalizeText(payload.length),
    selectedExchanges,
    selectedExchangesCount: selectedExchanges?.length,
  })
}

const sanitizeTelemetryContext = (context: TelemetryContext = {}): SanitizedTelemetryContext => {
  const actionCandidate = normalizeText(context.action)
  const feature =
    normalizeText(context.feature) ||
    (actionCandidate && SMART_SPOT_ACTIONS.has(actionCandidate) ? 'smart-spot' : undefined)
  const payloadSnapshot = buildSmartSpotPayloadSnapshot(context.payload)
  const selectedExchanges =
    normalizeStringArray(context.selectedExchanges) ||
    (Array.isArray(payloadSnapshot?.selectedExchanges) ? payloadSnapshot.selectedExchanges : undefined)

  const smartSpot =
    feature === 'smart-spot' || (actionCandidate && SMART_SPOT_ACTIONS.has(actionCandidate))
      ? pruneObject({
          prefillSourceType: normalizeText(context.prefillSourceType),
          prefillSourceId: normalizeText(context.prefillSourceId),
          requestId: normalizeText(context.requestId) || normalizeText(payloadSnapshot?.requestId),
          missingFields: normalizeStringArray(context.missingFields),
          aiRequired: normalizeBoolean(context.aiRequired),
          aiEnriched: normalizeBoolean(context.aiEnriched),
          smartSpotEditId:
            normalizeText(context.smartSpotEditId) || normalizeText(payloadSnapshot?.smartSpotEditId),
          publicationId: normalizeText(context.publicationId),
          selectedExchanges,
          selectedExchangesCount: selectedExchanges?.length,
          payload: payloadSnapshot,
        })
      : undefined

  const aiEmail =
    feature === 'ai-email' || (actionCandidate && AI_EMAIL_ACTIONS.has(actionCandidate))
      ? pruneObject({
          transportId: normalizeText(context.transportId),
          messageId: normalizeText(context.messageId),
          transportStatus: normalizeText(context.transportStatus),
          missingFields: normalizeStringArray(context.missingFields),
        })
      : undefined

  return {
    feature,
    action: actionCandidate,
    app: pruneObject({
      page: normalizeText(context.page),
      component: normalizeText(context.component),
      incidentRef: normalizeText(context.incidentRef),
    }),
    http: pruneObject({
      endpoint: normalizeText(context.endpoint),
      method: normalizeText(context.method)?.toUpperCase(),
      status: normalizeNumber(context.status),
      errorKey: normalizeText(context.errorKey),
      validationField: normalizeText(context.validationField),
      validationReason: normalizeText(context.validationReason),
    }),
    smartSpot,
    aiEmail,
    componentStack: normalizeText(context.componentStack),
  }
}

const applyScopeContext = (scope: Sentry.Scope, context: SanitizedTelemetryContext) => {
  scope.setTag('app_area', 'frontend-portal')
  if (context.feature) {
    scope.setTag('feature', context.feature)
  }
  if (context.action) {
    scope.setTag('action', context.action)
  }
  if (context.app?.incidentRef) {
    scope.setTag('incident_ref', String(context.app.incidentRef))
  }
  if (context.http?.endpoint) {
    scope.setTag('endpoint', String(context.http.endpoint))
  }
  if (context.http?.method) {
    scope.setTag('method', String(context.http.method))
  }
  if (context.http?.status != null) {
    scope.setTag('http_status', String(context.http.status))
  }
  if (context.http?.errorKey) {
    scope.setTag('error_key', String(context.http.errorKey))
  }
  if (context.smartSpot?.prefillSourceType) {
    scope.setTag('prefill_source_type', String(context.smartSpot.prefillSourceType))
  }
  if (context.smartSpot?.smartSpotEditId) {
    scope.setTag('smart_spot_edit_id', String(context.smartSpot.smartSpotEditId))
  }
  if (context.smartSpot?.publicationId) {
    scope.setTag('publication_id', String(context.smartSpot.publicationId))
  }
  if (context.aiEmail?.transportId) {
    scope.setTag('ai_email_transport_id', String(context.aiEmail.transportId))
  }
  if (context.aiEmail?.messageId) {
    scope.setTag('ai_email_message_id', String(context.aiEmail.messageId))
  }
  if (context.app) {
    scope.setContext('app', context.app)
  }
  if (context.http) {
    scope.setContext('http', context.http)
  }
  if (context.smartSpot) {
    scope.setContext('smartSpot', context.smartSpot)
  }
  if (context.aiEmail) {
    scope.setContext('aiEmail', context.aiEmail)
  }
  if (context.componentStack) {
    scope.setExtra('componentStack', context.componentStack)
  }
}

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error
  }
  return new Error(normalizeText(error) || 'Unknown frontend error')
}

const getErrorKey = (error: unknown): string | undefined => {
  if (!isRecord(error) || !isRecord(error.payload)) {
    return undefined
  }
  return normalizeText(error.payload.error ?? error.payload.message)
}

const isNetworkLikeError = (error: Error) =>
  /network|failed to fetch|connection refused|err_connection_refused|load failed/i.test(error.message || '')

// Browsers reject in-flight fetches with a plain TypeError ("Failed to fetch", "NetworkError…",
// "Load failed") — not an AbortError — once the page starts navigating away: reload, tab close,
// or UpdateBanner's post-deploy reload, which fires after the next route already sent its requests.
// The reloaded page repeats them, so these are not outages. The window expires because a started
// navigation can still be cancelled (download link, "leave page?" prompt).
const PAGE_UNLOAD_WINDOW_MS = 5000
let pageUnloadStartedAt = 0
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    pageUnloadStartedAt = Date.now()
  })
}
const isPageUnloading = () => pageUnloadStartedAt > 0 && Date.now() - pageUnloadStartedAt < PAGE_UNLOAD_WINDOW_MS

const isReadOnlyMethod = (method: unknown) => {
  const normalized = normalizeText(method)?.toUpperCase()
  return normalized === 'GET' || normalized === 'HEAD' || normalized === 'OPTIONS'
}

const attachIncidentRef = (error: unknown, incidentRef: string | null, captured = false) => {
  if (!incidentRef || !isRecord(error)) {
    return
  }
  if (typeof error.__incidentRef !== 'string' || !error.__incidentRef.trim()) {
    error.__incidentRef = incidentRef
  }
  if (captured) {
    error.__telemetryCaptured = true
  }
}

const captureExceptionWithContext = (
  error: Error,
  context: TelemetryContext,
  level: Sentry.SeverityLevel
) => {
  if (!isTelemetryEnabled()) {
    return null
  }
  const sanitizedContext = sanitizeTelemetryContext(context)
  return Sentry.withScope((scope) => {
    scope.setLevel(level)
    applyScopeContext(scope, sanitizedContext)
    return Sentry.captureException(error)
  })
}

const getStoredIncidentRef = (error?: unknown): string | null => {
  if (!isRecord(error)) {
    return null
  }
  const incidentRef = normalizeText(error.__incidentRef)
  return incidentRef || null
}

export const getCorrelationIdRef = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const correlationId = window.sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY)
    return correlationId && correlationId.trim() ? correlationId.trim() : null
  } catch {
    return null
  }
}

export const getIncidentRef = (error?: unknown): string | null => {
  const incidentRef = getStoredIncidentRef(error)
  if (incidentRef) {
    return incidentRef
  }
  return getCorrelationIdRef()
}

// Browser extensions (crypto wallets such as MetaMask) inject scripts that emit
// unhandled rejections unrelated to this app. Sentry already drops them via
// `denyUrls`, but our own backend reporting must filter them too, otherwise they
// pollute the production error registry.
const EXTENSION_STACK_PATTERN = /(?:chrome|moz|safari(?:-web)?)-extension:\/\//i
const EXTENSION_NOISE_MESSAGE_PATTERN =
  /metamask|web3|ethereum|solana|phantom|tronlink|keplr|coinbase ?wallet|walletconnect|evmask/i

const isExtensionNoiseError = (reason: unknown): boolean => {
  if (typeof reason === 'string') {
    return EXTENSION_NOISE_MESSAGE_PATTERN.test(reason)
  }
  if (!reason || typeof reason !== 'object') {
    return false
  }
  const candidate = reason as { message?: unknown; stack?: unknown }
  const stack = typeof candidate.stack === 'string' ? candidate.stack : ''
  if (EXTENSION_STACK_PATTERN.test(stack)) {
    return true
  }
  const message = typeof candidate.message === 'string' ? candidate.message : ''
  return EXTENSION_NOISE_MESSAGE_PATTERN.test(message)
}

// Sekret w adresie działa jak token na okaziciela (link z maila). Replay włącza się (albo nie) raz, przy
// ładowaniu strony — na takie trasy wolno więc wchodzić tylko pełnym ładowaniem; link do nich wewnątrz SPA
// wymaga najpierw Sentry.getReplay()?.stop(). Kontenery: PR4 dopisał '/c/offer/' i '/api/public/containers/offer/',
// PR6 '/c/driver/' i '/api/public/containers/driver/'.
export const TOKEN_PATH_PREFIXES = [
  '/contracts/accept/',
  '/licensing/accept/',
  '/contracts/accepted/',
  '/licensing/accepted/',
  '/c/offer/',
  '/c/driver/',
  '/api/public/contracts/session/',
  '/api/public/licensing/session/',
  '/api/public/contracts/acceptance/',
  '/api/public/licensing/acceptance/',
  '/api/public/containers/offer/',
  '/api/public/containers/driver/',
]
// Adres bywa też zakodowany w parametrze cudzego beacona (GA: dl=https%3A%2F%2F…), stąd %2F, %3F, %26, %3D.
// Tokeny w ścieżce są URL-safe (JWT, hex), więc segment kończy się na / ? # & albo % (zakodowany separator);
// wartość parametru — na & albo #. Oba też na " i \, żeby podmiana w JSON-ie nie psuła składni. Bez
// rozróżniania wielkości liter, bo React Router tak dopasowuje trasy.
const TOKEN_PATH_PATTERN = new RegExp(
  `(${TOKEN_PATH_PREFIXES.map((prefix) => prefix.replaceAll('/', '(?:/|%2F)')).join('|')})[^/?#&%\\s"\\\\]+`,
  'gi'
)
// ?token= resetu hasła, ?code=&state= powrotu OAuth (Trans, Microsoft 365), ?t= linku do PDF umowy. Te nazwy i prefiksy stron
// z TOKEN_PATH_PREFIXES (bez /api/) powtarza warunek przed GTM, gtag.js i Leadfeederem w index.html: trackery trzecie nie ładują
// się na takich trasach (zgodność pilnuje index.test.ts).
export const TOKEN_QUERY_PARAMS = ['token', 'code', 'state', 't']
// ?email= ze starych linków /login i /forgot-password to dane osobowe, nie sekret na okaziciela: w Sentry maskowany jak
// token (url, breadcrumby nawigacji from/to, spany), ale trackerów w index.html nie wyłącza — stąd poza TOKEN_QUERY_PARAMS.
const SCRUBBED_QUERY_PARAMS = [...TOKEN_QUERY_PARAMS, 'email']
const TOKEN_QUERY_PATTERN = new RegExp(`((?:[?&]|%3F|%26)(?:${SCRUBBED_QUERY_PARAMS.join('|')})(?:=|%3D))[^&#\\s"\\\\]+`, 'gi')

export const scrubTokenPath = (pathOrUrl: string) =>
  pathOrUrl.replace(TOKEN_PATH_PATTERN, '$1:token').replace(TOKEN_QUERY_PATTERN, '$1:token')

// Całość przez JSON, więc token znika z każdego pola: url, transaction, tagi, konteksty, breadcrumby, spany.
// sdkProcessingMetadata zostaje nietknięte — trzyma Scope (cykle), SDK kasuje je przed wysyłką, a DSC
// w nim łączy błąd z replayem. Czego nie da się zserializować, odrzucamy: wyjątek w beforeSend SDK
// wysłałoby jako błąd wewnętrzny bez beforeSend (z surowym request.url), a w beforeBreadcrumb wyleciałby
// do kodu aplikacji wołającego Sentry.addBreadcrumb.
const scrubTokenPathsIn = <T extends object>(value: T): T | null => {
  try {
    const { sdkProcessingMetadata, ...rest } = value as { sdkProcessingMetadata?: unknown }
    const scrubbed = JSON.parse(scrubTokenPath(JSON.stringify(rest))) as T
    return sdkProcessingMetadata === undefined ? scrubbed : { ...scrubbed, sdkProcessingMetadata }
  } catch {
    return null
  }
}

// page i endpoint idą też do rejestru błędów (bez sesji czekają w localStorage na zalogowanie).
const withoutTokenPaths = (context: TelemetryContext): TelemetryContext => ({
  ...context,
  page: scrubTokenPath(context.page || window.location.pathname),
  ...(context.endpoint ? { endpoint: scrubTokenPath(context.endpoint) } : {}),
})

export const initTelemetry = () => {
  if (telemetryInitialized || !isTelemetryEnabled()) {
    return
  }

  telemetryInitialized = true

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: normalizeText(import.meta.env.VITE_SENTRY_ENVIRONMENT) || import.meta.env.MODE,
    release: normalizeText(import.meta.env.VITE_SENTRY_RELEASE),
    sendDefaultPii: false,
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0),
    replaysSessionSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0),
    replaysOnErrorSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1),
    tracePropagationTargets: buildTracePropagationTargets(),
    denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /extensions\//i],
    integrations(defaultIntegrations) {
      const filtered = defaultIntegrations.filter((integration) => {
        const name = normalizeText((integration as { name?: unknown }).name)
        return name !== 'GlobalHandlers' && name !== 'Breadcrumbs'
      })
      // Replay nagrywa DOM i adresy (href strony, nawigacje, sieć) z pominięciem beforeSend, więc na stronie
      // otwartej z linku z sekretem nie startuje.
      const pageUrl = window.location.pathname + window.location.search
      return [
        ...filtered,
        Sentry.browserTracingIntegration(),
        Sentry.breadcrumbsIntegration({ console: false }),
        ...(scrubTokenPath(pageUrl) === pageUrl ? [Sentry.replayIntegration()] : []),
      ]
    },
    beforeBreadcrumb: scrubTokenPathsIn,
    beforeSendTransaction: scrubTokenPathsIn,
    // Samodzielne spany (np. INP) omijają beforeSendTransaction. null = SDK wysyła oryginał, więc szkielet.
    beforeSendSpan: (span) => scrubTokenPathsIn(span) ?? { ...span, description: '', data: {} },
    beforeSend(event) {
      if (!event.request) {
        return scrubTokenPathsIn(event)
      }
      const request = { ...event.request }
      delete request.headers
      delete request.cookies
      delete request.data
      if (typeof request.url === 'string') {
        try {
          const parsed = new URL(request.url, window.location.origin)
          request.url = `${parsed.origin}${parsed.pathname}`
        } catch {
          // Keep the original URL when parsing fails.
        }
      }
      return scrubTokenPathsIn({ ...event, request })
    },
  })

  if (!unhandledRejectionBound && typeof window !== 'undefined') {
    window.addEventListener('cargonice-client-error-delivery-failed', (event) => {
      if (!isTelemetryEnabled()) {
        return
      }
      const detail = isRecord((event as CustomEvent).detail) ? ((event as CustomEvent).detail as Record<string, unknown>) : {}
      const incidentRef = normalizeText(detail.incidentRef)
      const status = normalizeNumber(detail.status)
      const endpointPath = normalizeText(detail.endpointPath)
      const breadcrumbData = pruneObject({
        incidentRef,
        status,
        endpointPath,
        errorName: normalizeText(detail.errorName),
      })
      Sentry.addBreadcrumb({
        category: 'frontend-diagnostics',
        message: 'client_error_registry_delivery_failed',
        level: 'warning',
        ...(breadcrumbData ? { data: breadcrumbData } : {}),
      })
      // Bez statusu to zanik sieci — ten sam, który wywołał zgłaszany błąd; zdarzenie czeka w kolejce
      // localStorage i dochodzi później. Alarmujemy tylko, gdy rejestr faktycznie odrzucił zgłoszenie.
      if (status == null) {
        return
      }
      Sentry.withScope((scope) => {
        if (incidentRef) {
          scope.setTag('incident_ref', incidentRef)
        }
        scope.setTag('http_status', String(status))
        if (endpointPath) {
          scope.setTag('endpoint', endpointPath)
        }
        Sentry.captureMessage('client_error_registry_delivery_failed', 'warning')
      })
    })
    window.addEventListener('unhandledrejection', (event) => {
      if (normalizeText((event.reason as { name?: unknown } | undefined)?.name) === 'AbortError') {
        return
      }
      if (isExtensionNoiseError(event.reason)) {
        return
      }
      captureAppError(toError(event.reason), {
        feature: 'portal-ui',
        action: 'unhandledrejection',
        page: window.location.pathname,
      })
    })
    unhandledRejectionBound = true
  }
}

export const syncTelemetryUser = (profile: PortalProfile | null) => {
  if (!isTelemetryEnabled()) {
    return
  }

  if (!profile) {
    Sentry.setUser(null)
    return
  }

  const userId = normalizeText(profile.id)
  const companyId = normalizeText(profile.company?.id)

  Sentry.setUser(userId ? { id: userId } : null)
  if (companyId) {
    Sentry.setTag('company_id', companyId)
  }
}

export const addFeatureBreadcrumb = (message: string, context: TelemetryContext = {}) => {
  if (!isTelemetryEnabled()) {
    return
  }
  const sanitizedContext = sanitizeTelemetryContext(context)
  const breadcrumbData = pruneObject({
    feature: sanitizedContext.feature,
    action: sanitizedContext.action,
    ...(sanitizedContext.http ? { http: sanitizedContext.http } : {}),
    ...(sanitizedContext.smartSpot ? { smartSpot: sanitizedContext.smartSpot } : {}),
    ...(sanitizedContext.aiEmail ? { aiEmail: sanitizedContext.aiEmail } : {}),
    ...(sanitizedContext.app ? { app: sanitizedContext.app } : {}),
  })

  Sentry.addBreadcrumb({
    category:
      sanitizedContext.feature === 'smart-spot'
        ? 'smart-spot'
        : sanitizedContext.feature === 'ai-email'
          ? 'ai-email'
          : 'frontend-portal',
    message,
    level: 'info',
    ...(breadcrumbData ? { data: breadcrumbData } : {}),
  })
}

export const captureAppError = (error: unknown, context: TelemetryContext = {}) => {
  const existingIncidentRef = getStoredIncidentRef(error)
  if (isRecord(error) && error.__telemetryCaptured === true && existingIncidentRef) {
    return existingIncidentRef
  }

  const errorObject = toError(error)
  const incidentRef = existingIncidentRef || createClientIncidentRef()
  const safeContext = withoutTokenPaths(context)
  reportClientErrorEvent(error, safeContext, {
    category: context.action === 'render-crash' ? 'frontend_render_error' : 'frontend_error',
    severity: 'ERROR',
    stackTraceShort: errorObject.stack,
    incidentRef,
  })
  captureExceptionWithContext(errorObject, { ...safeContext, incidentRef }, 'error')
  attachIncidentRef(errorObject, incidentRef, Boolean(incidentRef))
  if (error !== errorObject) {
    attachIncidentRef(error, incidentRef, Boolean(incidentRef))
  }
  return incidentRef
}

export const captureApiError = (error: unknown, context: TelemetryContext = {}) => {
  const existingIncidentRef = getStoredIncidentRef(error)
  if (isRecord(error) && error.__telemetryCaptured === true && existingIncidentRef) {
    return existingIncidentRef
  }

  const errorObject = toError(error)
  const status = normalizeNumber(isRecord(error) ? error.status : undefined)
  if (status == null && isNetworkLikeError(errorObject) && isPageUnloading()) {
    return null
  }
  const statuslessReadOnlyNetworkError =
    status == null && isReadOnlyMethod(context.method) && isNetworkLikeError(errorObject)
  const level: Sentry.SeverityLevel =
    (status != null && status >= 400 && status < 500) || statuslessReadOnlyNetworkError ? 'warning' : 'error'
  const incidentRef = existingIncidentRef || createClientIncidentRef()
  const safeContext = withoutTokenPaths(context)
  reportClientErrorEvent(error, safeContext, {
    category: 'frontend_api_error',
    severity: (status != null && status >= 400 && status < 500) || statuslessReadOnlyNetworkError ? 'WARN' : 'ERROR',
    httpStatus: status,
    stackTraceShort: errorObject.stack,
    incidentRef,
  })
  captureExceptionWithContext(
    errorObject,
    {
      ...safeContext,
      incidentRef,
      ...(context.status == null && status != null ? { status } : {}),
      ...(context.errorKey == null && getErrorKey(error) ? { errorKey: getErrorKey(error) } : {}),
    },
    level
  )
  attachIncidentRef(errorObject, incidentRef, Boolean(incidentRef))
  if (error !== errorObject) {
    attachIncidentRef(error, incidentRef, Boolean(incidentRef))
  }
  return incidentRef
}
